import { DeleteOutlined, EnvironmentOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Divider, message, Modal, Spin, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { ApiHandler } from '../services/apiHandler';
interface ViewRfSimulationProps {
  close: () => void;
  createNewSimulation: () => void;
  viewSimulation: (simulation: any) => void;
}

const ViewRfSimulation: React.FC<ViewRfSimulationProps> = (props) => {
  const [rfSimulations, setRfSimulations] = useState([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    getAllSimulations();
  }, []);

  const getAllSimulations = () => {
    setIsLoading(true);
    ApiHandler({
      apiName: 'getRFSimulations',
      params: {},
      body: {},
    })
      .then((response: any) => {
        setRfSimulations(response.data.simulations);
      })
      .catch((error: any) => {
        message.error(error?.data?.message ?? error?.message ?? 'Failed to get RF simulations');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteSimulation = (id: number | null) => {
    if (!id) return;
    setDeleteConfirmationVisible(false);
    ApiHandler({
      apiName: 'deleteRFSimulationById',
      params: { id: id },
      body: {},
    })
      .then((response: any) => {
        message.success('RF simulation deleted successfully');
      })
      .catch((error: any) => {
        message.error(error?.data?.message ?? error?.message ?? 'Failed to delete RF simulation');
      })
      .finally(() => {
        getAllSimulations();
      });
  };

  const viewSimulation = (simulation: any) => {
    props.viewSimulation(simulation);
  };

  return (
    <div className="position-absolute d-flex flex-column gap-2 overflow-auto" style={{ top: '16px', left: '16px', bottom: '16px', width: '400px' }}>
      <Card id="rf-simulation-view-card">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <Typography.Title level={5}>RF Simulations</Typography.Title>
          <Button
            type="link"
            color="danger"
            style={{ color: 'red' }}
            onClick={() => {
              props.close();
            }}>
            Cancel
          </Button>
        </div>
        <Divider />
        <Button
          type="primary"
          className="w-100"
          onClick={() => {
            props.createNewSimulation();
          }}>
          Create New RF Simulation
        </Button>

        {isLoading && (
          <div className="d-flex justify-content-center align-items-center py-4">
            <Spin spinning={isLoading} />
          </div>
        )}

        <div className="d-flex flex-column gap-2 overflow-auto p-2">
          {[...rfSimulations]
            .sort((a: any, b: any) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return dateB - dateA; // descending order (newest first)
            })
            .map((simulation: any) => (
              <>
                <Card
                  size="small"
                  title={
                    <div className="d-flex flex-row justify-content-between align-items-center">
                      <Typography.Title level={5}>{simulation.simulation_name}</Typography.Title>
                      <div className="d-flex flex-row gap-1">
                        <Button
                          type="link"
                          color="danger"
                          size="small"
                          onClick={() => {
                            setDeleteConfirmationVisible(true);
                            setDeleteConfirmationId(simulation.id);
                          }}>
                          <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                        <Button
                          type="link"
                          color="primary"
                          size="small"
                          onClick={() => {
                            viewSimulation(simulation);
                          }}>
                          <EyeOutlined />
                        </Button>
                      </div>
                    </div>
                  }
                  key={simulation.id}
                  className="d-flex flex-column gap-2 mt-3">
                  <div className="d-flex flex-column gap-1">
                    <div className="d-flex flex-row gap-1">
                      <EnvironmentOutlined />
                      <Typography.Text strong>Location : </Typography.Text>
                      <Typography.Text type="secondary">{`${simulation.location.latitude.toFixed(6)}, ${simulation.location.longitude.toFixed(
                        6
                      )}`}</Typography.Text>
                    </div>
                  </div>
                </Card>
              </>
            ))}
        </div>
      </Card>
      <Modal
        open={deleteConfirmationVisible}
        title="Delete RF Simulation"
        okText="Delete"
        cancelText="Cancel"
        onCancel={() => setDeleteConfirmationVisible(false)}
        onOk={() => deleteSimulation(deleteConfirmationId ?? null)}>
        <Typography.Text>Are you sure you want to delete the RF simulation?</Typography.Text>
      </Modal>
    </div>
  );
};

export default ViewRfSimulation;
